import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { RELATIONSHIP_OPTIONS } from '../../constants/relationshipOptions';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: string;
  notes?: string;
}

interface ManualContactModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (contact: Contact) => Promise<void>;
  loading?: boolean;
  initialData?: Partial<Contact>;
}

const ManualContactModal: React.FC<ManualContactModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  initialData
}) => {
  const [formData, setFormData] = useState<Contact>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: '',
    notes: ''
  });
  const [error, setError] = useState<string | null>(null);
  
  // Track if modal was previously open to detect open/close transitions
  const wasOpenRef = useRef(false);

  // Populate form ONLY when modal transitions from closed to open
  // This prevents form data from being reset while user is actively editing
  useEffect(() => {
    // Only populate form when modal opens (transition from closed to open)
    if (open && !wasOpenRef.current) {
      if (initialData) {
        setFormData({
          id: initialData.id || '',
          firstName: initialData.firstName || '',
          lastName: initialData.lastName || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          relationship: initialData.relationship || '',
          notes: initialData.notes || ''
        });
      } else {
        // Reset form when opening with no initial data (new contact)
        setFormData({
          id: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          relationship: '',
          notes: ''
        });
      }
      setError(null);
    }
    
    // Update the ref to track current open state
    wasOpenRef.current = open;
  }, [open, initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      setError('Either email or phone is required');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Invalid email format');
      return false;
    }
    if (formData.phone && !/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      setError('Invalid phone format');
      return false;
    }
    if (!formData.relationship.trim()) {
      setError('Relationship is required');
      return false;
    }
    return true;
  };

  const submitContact = async () => {
    if (loading) return;
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const contact: Contact = {
        ...formData,
        id: `manual-${Date.now()}`
      };
      await onSubmit(contact);

      setFormData({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        relationship: '',
        notes: ''
      });
      onClose();
    } catch {
      setError('Failed to add contact');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitContact();
  };

  const relationshipOptions = [
    { value: '', label: 'Select relationship' },
    ...RELATIONSHIP_OPTIONS.map(r => ({ value: r.toLowerCase(), label: r })),
  ];

  const isEditing = !!initialData;

  return (
    <Modal
      open={open}
      title={isEditing ? 'Edit Contact' : 'Add Contact Manually'}
      onOk={() => void submitContact()}
      onCancel={onClose}
      okText={loading ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Contact')}
      cancelText="Cancel"
      okDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                disabled={loading}
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                disabled={loading}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                disabled={loading}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Relationship *
            </label>
            <select
              name="relationship"
              value={formData.relationship}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              disabled={loading}
            >
              {relationshipOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              disabled={loading}
              placeholder="Add any additional information..."
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

        </form>
    </Modal>
  );
};

export default ManualContactModal;
